import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar } from "primereact/calendar";

export default function DatePickerField({
  label,
  value,
  onChange,
  placeholder = "Select a date",
  dateFormat = "d M yy", // e.g. 21 Jan 2025
  formatDisplayValue,
  appendTo = "self", // or document.body if you want
  className = "",
  inputClassName = "",
  inputId,
  disabled = false,
  hideTriggerButton = false,
  stagedView = false, // year → month → day progression
  maxDate,
}) {
  const calRef = useRef(null);
  const wrapRef = useRef(null);
  const stagedOverlayRef = useRef(null);
  const keepStagedPickerOpenRef = useRef(false);
  const [stage, setStage] = useState("year");
  const [pickerVisible, setPickerVisible] = useState(false);
  const [stagedOverlayStyle, setStagedOverlayStyle] = useState(null);
  const [viewDate, setViewDate] = useState(value || new Date());

  useEffect(() => {
    if (!stagedView || !pickerVisible) return undefined;

    function handlePointerDown(event) {
      const target = event.target;
      if (
        wrapRef.current?.contains(target) ||
        stagedOverlayRef.current?.contains(target)
      ) {
        return;
      }
      closeStagedPicker();
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") closeStagedPicker();
    }

    function handleViewportChange() {
      positionStagedOverlay();
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [pickerVisible, stagedView]);

  function formatDisplayDate(dateValue) {
    if (!dateValue) return "";
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    if (formatDisplayValue) return formatDisplayValue(date);
    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  function positionStagedOverlay() {
    if (typeof window === "undefined" || !wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    const panelWidth = 324;
    const panelHeight = 334;
    const viewportPad = 12;
    const gap = 8;
    const left = Math.min(
      Math.max(rect.left, viewportPad),
      window.innerWidth - panelWidth - viewportPad
    );
    const belowTop = rect.bottom + gap;
    const top =
      belowTop + panelHeight <= window.innerHeight - viewportPad
        ? belowTop
        : Math.max(viewportPad, rect.top - panelHeight - gap);

    setStagedOverlayStyle({
      left: `${left}px`,
      top: `${top}px`,
      width: `${panelWidth}px`,
    });
  }

  function keepPickerOpenForNextStage() {
    keepStagedPickerOpenRef.current = true;
    setPickerVisible(true);
  }

  function openStagedPicker() {
    if (disabled) return;
    keepStagedPickerOpenRef.current = false;
    setStage("year");
    setViewDate(value || new Date());
    positionStagedOverlay();
    setPickerVisible(true);
  }

  function closeStagedPicker() {
    keepStagedPickerOpenRef.current = false;
    setPickerVisible(false);
    setStage("year");
  }

  function handleStagedChange(e) {
    const next = e.value || null;
    if (!next) return;

    if (stage === "year") {
      setViewDate(next);
      setStage("month");
      keepPickerOpenForNextStage();
      return;
    }
    if (stage === "month") {
      setViewDate(next);
      setStage("date");
      keepPickerOpenForNextStage();
      return;
    }
    // date stage — commit and close
    keepStagedPickerOpenRef.current = false;
    onChange?.(next);
    closeStagedPicker();
  }

  function handleClearKeyDown(event) {
    if (disabled || event.key !== "Backspace") return;
    if (!value) return;
    event.preventDefault();
    event.stopPropagation();
    onChange?.(null);
    closeStagedPicker();
  }

  function renderStagedOverlay() {
    if (!stagedView || !pickerVisible || typeof document === "undefined") {
      return null;
    }

    return createPortal(
      <div
        ref={stagedOverlayRef}
        className={`clientsDateStagedPortal clientsDateStagedPortal--${stage}`}
        style={stagedOverlayStyle || undefined}
      >
        <Calendar
          key={stage}
          value={value}
          onChange={handleStagedChange}
          view={stage}
          viewDate={viewDate}
          onViewDateChange={(e) => setViewDate(e.value || viewDate)}
          inline
          showIcon={false}
          dateFormat={dateFormat}
          maxDate={maxDate}
          className="clientsDateStagedInlineCalendar"
        />
      </div>,
      document.body
    );
  }

  if (stagedView) {
    return (
      <div className={`mb-3 ${className}`}>
        {label && <label className="form-label">{label}</label>}

        <div className="clientsDateWrap" ref={wrapRef}>
          <input
            id={inputId}
            type="text"
            className={`form-control clientsDateInput ${inputClassName}`}
            value={formatDisplayDate(value)}
            placeholder={placeholder}
            readOnly
            disabled={disabled}
            onFocus={openStagedPicker}
            onClick={openStagedPicker}
            onKeyDown={handleClearKeyDown}
          />

          {!hideTriggerButton ? (
            <button
              type="button"
              className="clientsDateBtn"
              onClick={openStagedPicker}
              aria-label="Open calendar"
              title="Open calendar"
              disabled={disabled}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                className="clientsDateIcon"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h.5A1.5 1.5 0 0 1 15 2.5v11A1.5 1.5 0 0 1 13.5 15h-11A1.5 1.5 0 0 1 1 13.5v-11A1.5 1.5 0 0 1 2.5 1H3V.5a.5.5 0 0 1 .5-.5zM2 6v7.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V6H2z" />
              </svg>
            </button>
          ) : null}

          {renderStagedOverlay()}
        </div>
      </div>
    );
  }

  return (
    <div className={`mb-3 ${className}`}>
      {label && <label className="form-label">{label}</label>}

      <div className="clientsDateWrap">
        <Calendar
          ref={calRef}
          inputId={inputId}
          value={value}
          onChange={(e) => onChange?.(e.value)}
          dateFormat={dateFormat}
          showIcon={false}
          placeholder={placeholder}
          appendTo={appendTo === "body" ? document.body : undefined}
          className="w-100"
          disabled={disabled}
          inputClassName={`form-control clientsDateInput ${inputClassName}`}
          maxDate={maxDate}
          onKeyDown={handleClearKeyDown}
        />

        {!hideTriggerButton ? (
          <button
            type="button"
            className="clientsDateBtn"
            onClick={() => calRef.current?.show()}
            aria-label="Open calendar"
            title="Open calendar"
            disabled={disabled}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              className="clientsDateIcon"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h.5A1.5 1.5 0 0 1 15 2.5v11A1.5 1.5 0 0 1 13.5 15h-11A1.5 1.5 0 0 1 1 13.5v-11A1.5 1.5 0 0 1 2.5 1H3V.5a.5.5 0 0 1 .5-.5zM2 6v7.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V6H2z" />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
}
